'use client';

import { LoadingButton } from '@mui/lab';
import { Box, Button, Divider, InputAdornment, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DatePicker, MobileTimePicker } from '@mui/x-date-pickers';
import GoogleMaterialIcon from 'components/google-icon';
import { RHFTextField } from 'components/hook-form';
import { AppointmentContext } from 'context/AppointmentContext';
import { useTranslate } from "locales";
import { useContext } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import CountryMasterAutocomplete from '../CountryMasterAutocomplete';
import StateMasterAutocomplete from '../StateMasterAutocomplete';
import VehicleClassificationTypeAutocomplete from '../VehicleClassificationTypeAutocomplete';

const requireFeildCss = { '& .MuiInputBase-root': { bgcolor: "background.paper" } }

export default function BookingForm({ referenceData }) {
    let { companyId } = useContext(AppointmentContext)
    const { t } = useTranslate();
    const {
        setValue,
        control,
        watch,
        formState: { isDirty, isSubmitting }
    } = useFormContext()

    let country = watch('country')

    return (
        <>
            <Box display={"flex"} flexDirection={"column"} sx={{ p: 2 }}>
                <Box display={'flex'} flexDirection='column' gap={3} pt={3}>
                    <Grid container spacing={2} rowSpacing={3}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Controller
                                name="date"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <DatePicker
                                        disabled
                                        {...field}
                                        className='required'
                                        sx={{ ...requireFeildCss }}
                                        label={t('date')}
                                        format="dd/MM/yyyy"
                                        slotProps={{ textField: { fullWidth: true, error: !!error, helperText: error?.message, } }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4 }}>
                            <Controller
                                name="from_time"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <MobileTimePicker
                                        {...field}
                                        disabled
                                        className='required'
                                        sx={{ ...requireFeildCss }}
                                        ampmInClock
                                        label={t('from_time')}
                                        slotProps={{ textField: { fullWidth: true, error: !!error, helperText: error?.message, } }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4 }}>
                            <Controller
                                name="to_time"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <MobileTimePicker
                                        {...field}
                                        disabled
                                        className='required'
                                        sx={{ ...requireFeildCss }}
                                        ampmInClock
                                        label={t("to_time")}
                                        slotProps={{ textField: { fullWidth: true, error: !!error, helperText: error?.message, } }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Divider sx={{ borderColor: '#E5E7EB' }} />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant='subtitle2'>{t('customer_information')}</Typography>
                        </Grid>
                        <Grid size={{ xs: 4, md: 4 }}>
                            <RHFTextField name="first_name" label={t("first_name")} autoFocus autoComplete='off' sx={{ ...requireFeildCss }} />
                        </Grid>
                        <Grid size={{ xs: 4, md: 4 }}>
                            <RHFTextField name="last_name" label={t("last_name")} />
                        </Grid>
                        <Grid size={{ xs: 4, md: 4 }}>
                            <RHFTextField name="company_name" label={t("company_name")} />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <RHFTextField
                                name="phone"
                                label={t("phone_number")}
                                sx={{ ...requireFeildCss }}
                                InputProps={{ startAdornment: (<InputAdornment position="start"><Typography variant="body2" component="span" sx={{ color: "text.disabled" }}>{`+${country?.phonecode || ""}`}</Typography></InputAdornment>), }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <RHFTextField name="email" label={t("email_address")} InputProps={{ startAdornment: (<InputAdornment position="start"><GoogleMaterialIcon icon={'alternate_email'} fontSize="small" /></InputAdornment>) }} />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <CountryMasterAutocomplete
                                className='required'
                                sx={{ ...requireFeildCss }}
                                // isDisabled={isSetCompanyInfo}
                                changeCountry={(newValue) => {
                                    setValue("country", newValue, { shouldDirty: true });
                                    setValue("state", null, { shouldDirty: true });
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4.5 }}>
                            <StateMasterAutocomplete
                                name="state"
                                sx={{ ...requireFeildCss }}
                                className='required'
                                currentCountry={watch("country")}
                                changeItem={(newValue) => setValue("state", newValue, { shouldDirty: true, shouldValidate: true })}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4.5 }}>
                            <RHFTextField name="city" label={t("city")} sx={{ ...requireFeildCss }} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <RHFTextField name="address_line1" multiline minRows={2} label={t("address_line")} sx={{ ...requireFeildCss }} />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Divider sx={{ borderColor: '#E5E7EB' }} />
                        </Grid>
                        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant='subtitle2'>{t('vehicle_information')}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <RHFTextField name="make" label={t("make")} sx={{ ...requireFeildCss }} />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <RHFTextField name="model" label={t("model")} sx={{ ...requireFeildCss }} />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <RHFTextField name="license_plate" label={t("license_plate")} sx={{ ...requireFeildCss }} />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }} >
                            <VehicleClassificationTypeAutocomplete
                                workshopType={watch('workshop_type')}
                                label={t('body_type')}
                                companyId={companyId}
                                sx={{ ...requireFeildCss }}
                                changeItem={(vehicleClassification) => setValue("vehicle_classification_type", vehicleClassification, { shouldDirty: true, shouldValidate: true })}
                                className='required'
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <RHFTextField multiline minRows={2} name="notes" label={t("notes")} />
                        </Grid>
                    </Grid>
                </Box>
                <Box display={"flex"} mt={2} alignItems="center" gap={1} justifyContent="flex-end">
                    <LoadingButton disabled={!isDirty} loading={isSubmitting} type="submit" variant="contained" color="primary" sx={{ minWidth: 120 }}>
                        {t("save")}
                    </LoadingButton>
                </Box>
            </Box>
        </>
    )
}